import React from 'react';
import { MapPin, Phone, Mail, Clock, Instagram, Facebook } from 'lucide-react';
import { useLang } from '@/lib/LangContext';

const WA_NUMBER = '+995 591 96 99 01';
const EMAIL = 'guga@smartpaw.ge';

export default function ContactsSection() {
  const { t } = useLang();
  const cs = t.contactSection || {};
  const legacy = t.contact || {};

  const addressValue = legacy.addressValue || '0102 Tsereteli Ave. 118, Tbilisi, Georgia';
  const hoursValue = legacy.hoursValue || 'Mon–Sat · 09:00–19:00';

  const items = [
    { icon: <MapPin size={20} />, label: cs.addressLabel, value: addressValue, href: 'https://maps.google.com/?q=0102+Tsereteli+Ave+118+Tbilisi' },
    { icon: <Phone size={20} />, label: cs.phoneLabel, value: WA_NUMBER, href: 'tel:+995591969901' },
    { icon: <Mail size={20} />, label: cs.emailLabel, value: EMAIL, href: `mailto:${EMAIL}` },
    { icon: <Clock size={20} />, label: cs.hoursLabel, value: hoursValue, href: null },
  ];

  return (
    <section id="contact" className="relative py-14 md:py-20 bg-[#FDFBF7]" data-testid="contacts-section">
      <div className="max-w-6xl mx-auto px-5 md:px-10">
        <div className="text-center max-w-2xl mx-auto">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[#0A4D8C1A] text-xs font-bold uppercase text-[#0A4D8C] mb-5">
            <span className="w-1.5 h-1.5 rounded-full bg-[#F25C05]" />
            {cs.eyebrow}
          </div>
          <h2 className="font-display font-extrabold text-[#05223D] text-3xl md:text-4xl tracking-[-0.02em]">
            {cs.title}
          </h2>
          <p className="mt-4 text-[#465B70] leading-relaxed">
            {cs.body}
          </p>
        </div>

        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {items.map((it) => {
            const content = (
              <>
                <span className="w-12 h-12 rounded-2xl bg-[#0A4D8C]/8 text-[#0A4D8C] flex items-center justify-center shrink-0">{it.icon}</span>
                <div className="min-w-0">
                  <p className="text-[11px] font-bold uppercase tracking-wider text-[#465B70]">{it.label}</p>
                  <p className="mt-1 text-[#05223D] font-semibold text-sm md:text-[15px] leading-snug break-words">{it.value}</p>
                </div>
              </>
            );
            const className = 'card-soft bg-white rounded-2xl p-5 md:p-6 border border-[#eadfce] flex items-start gap-4 transition-colors';
            return it.href ? (
              <a
                key={it.label}
                href={it.href}
                target={it.href.startsWith('http') ? '_blank' : undefined}
                rel={it.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                className={`${className} hover:border-[#0A4D8C]/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0A4D8C]/40`}
                data-testid={`contact-card-${it.label}`}
              >
                {content}
              </a>
            ) : (
              <div key={it.label} className={className} data-testid={`contact-card-${it.label}`}>
                {content}
              </div>
            );
          })}
        </div>

        <div className="mt-10 flex items-center justify-center gap-2">
          <a href="https://www.instagram.com/smartpaw__/" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="w-11 h-11 rounded-full border border-[#0A4D8C1A] text-[#0A4D8C] flex items-center justify-center hover:bg-[#0A4D8C] hover:text-white hover:border-[#0A4D8C] transition-all" data-testid="contact-ig-link">
            <Instagram className="w-4 h-4" />
          </a>
          <a href="https://www.facebook.com/yoursmartpaw" target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="w-11 h-11 rounded-full border border-[#0A4D8C1A] text-[#0A4D8C] flex items-center justify-center hover:bg-[#0A4D8C] hover:text-white hover:border-[#0A4D8C] transition-all" data-testid="contact-fb-link">
            <Facebook className="w-4 h-4" />
          </a>
        </div>
      </div>
    </section>
  );
}
